import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Pencil, Loader2, Cpu } from "lucide-react";
import betaAPI from "v2/api";
import { showToast } from "utils/toast";
import BetaNavbar from "./components/BetaNavbar";
import InfoField from "./components/Manufacturing/InfoField";
import WearableStatusBadge from "./components/Manufacturing/WearableStatusBadge";
import StatusControl from "./components/Manufacturing/StatusControl";
import ValidationFlow from "./components/Manufacturing/ValidationFlow";
import AttemptHistory from "./components/Manufacturing/AttemptHistory";
import SessionHistory from "./components/Manufacturing/SessionHistory";
import SessionWearablesModal from "./components/Manufacturing/SessionWearablesModal";
import WearableFormModal from "./components/Manufacturing/WearableFormModal";
import {
  formatDateTime,
  formatMac,
  getStageMeta,
  formatApiError,
  wearableIdentity,
  fieldLabelDisplay,
  getUnitTypeLabel,
} from "./components/Manufacturing/constants";

const WearableDetail = () => {
  const { t } = useTranslation();
  const { bleMac } = useParams();
  const navigate = useNavigate();

  const [wearable, setWearable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  // Session opened from the Session History card; shows every unit assigned
  // in that session.
  const [openSessionId, setOpenSessionId] = useState(null);
  // Bumped whenever an attempt is logged or the status changes, so the audit
  // trail refetches.
  const [historyKey, setHistoryKey] = useState(0);

  const fetchWearable = useCallback(async () => {
    try {
      const res = await betaAPI.getWearable({ bleMac });
      setWearable(res.data);
    } catch (err) {
      if (err?.status === 404) {
        setNotFound(true);
      } else {
        showToast.error(
          formatApiError(err, t("admin.wearableDetail.loadFailed"))
        );
      }
    } finally {
      setLoading(false);
    }
  }, [bleMac]);

  useEffect(() => {
    fetchWearable();
  }, [fetchWearable]);

  // Step attempts and status changes both return the full updated detail — swap
  // it in wholesale so the tree (and possibly the status) re-render, and refresh
  // the audit trail.
  const handleUpdated = (updated) => {
    if (updated) setWearable(updated);
    setHistoryKey((k) => k + 1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <BetaNavbar hideWorkspaceSelector />
        <div className="flex items-center justify-center py-20">
          <Loader2 size={22} className="animate-spin text-[#E07A3A]" />
        </div>
      </div>
    );
  }

  if (notFound || !wearable) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <BetaNavbar hideWorkspaceSelector />
        <div className="mx-auto max-w-[1440px] px-6 py-20 text-center">
          <p className="text-sm text-[#6B7280]">
            {t("admin.wearableDetail.notFound")}
          </p>
          <button
            onClick={() => navigate("/v2/admin/manufacturing")}
            className="mt-4 text-sm font-medium text-[#E07A3A]"
          >
            {t("admin.wearableDetail.backToManufacturing")}
          </button>
        </div>
      </div>
    );
  }

  const inValidation = wearable.status === "in_validation";
  const stageMeta = getStageMeta(wearable.validation_stage);
  const identity = wearableIdentity(wearable);

  return (
    <div className="min-h-screen overscroll-none bg-[#F8F9FA]">
      <BetaNavbar hideWorkspaceSelector />

      <main>
        {/* Hero */}
        <div
          style={{
            background:
              "linear-gradient(180deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 50%, rgba(255, 255, 255, 0) 100%)",
          }}
        >
          <div className="mx-auto max-w-[1440px] px-6 pb-6 pt-5">
            <button
              onClick={() => navigate("/v2/admin/manufacturing")}
              className="mb-3 flex items-center gap-1 text-xs font-medium text-[#6B7280] transition-colors duration-150 hover:text-[#E07A3A]"
            >
              <ChevronLeft size={14} />
              {t("admin.wearableDetail.breadcrumb")}
            </button>

            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-sm">
                  <Cpu size={24} className="text-[#E07A3A]" />
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-[26px] font-bold leading-tight text-[#111827]">
                      {identity.primary}
                    </h1>
                    <WearableStatusBadge status={wearable.status} size="md" />
                    {inValidation && wearable.validation_stage && (
                      <span className="rounded bg-[#F3F4F6] px-2 py-0.5 text-[11px] font-medium text-[#6B7280]">
                        {stageMeta.label}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 font-mono text-xs text-[#6B7280]">
                    {identity.secondary ? `${identity.secondary} · ` : ""}
                    {formatMac(wearable.ble_mac)}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowEdit(true)}
                className="flex items-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2.5 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
              >
                <Pencil size={14} />
                {t("common.actions.edit")}
              </button>
            </div>
          </div>
        </div>

        <div className="mx-auto grid max-w-[1440px] gap-4 px-6 pb-6 lg:grid-cols-3">
          {/* Left: details + lifecycle */}
          <div className="flex flex-col gap-4 lg:col-span-1">
            <div className="rounded-xl border border-[#F3F4F6] bg-white p-5">
              <h2 className="mb-4 text-sm font-semibold text-[#111827]">
                {t("admin.wearableDetail.details")}
              </h2>
              <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                <InfoField
                  label={t("admin.wearableDetail.batch")}
                  value={
                    wearable.batch_code ? (
                      <button
                        onClick={() =>
                          navigate(
                            `/v2/admin/manufacturing/batches/${wearable.batch_id}`
                          )
                        }
                        className="font-medium text-[#E07A3A] hover:underline"
                      >
                        {wearable.batch_code}
                      </button>
                    ) : null
                  }
                />
                <InfoField
                  label={t("admin.wearableDetail.unitType")}
                  value={
                    <span className="capitalize">
                      {wearable.unit_type
                        ? getUnitTypeLabel(wearable.unit_type)
                        : wearable.unit_type}
                    </span>
                  }
                />
                <InfoField
                  label={t("admin.wearableDetail.printedLabel")}
                  value={fieldLabelDisplay(wearable) || "—"}
                  mono
                />
                <InfoField
                  label={t("admin.wearableDetail.serial")}
                  value={wearable.serial}
                  mono
                />
                <InfoField
                  className="col-span-2"
                  label={t("admin.wearableDetail.bleMac")}
                  value={wearable.ble_mac}
                  mono
                />
              </div>

              <div className="my-4 border-t border-[#F3F4F6]" />

              <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                <InfoField
                  label={t("admin.wearableDetail.validated")}
                  value={formatDateTime(wearable.validated_at)}
                />
                <InfoField
                  label={t("admin.wearableDetail.deployed")}
                  value={formatDateTime(wearable.deployed_at)}
                />
                <InfoField
                  label={t("admin.wearableDetail.retired")}
                  value={formatDateTime(wearable.retired_at)}
                />
                <InfoField
                  label={t("admin.wearableDetail.created")}
                  value={formatDateTime(wearable.created_at)}
                />
                <InfoField
                  className="col-span-2"
                  label={t("admin.wearableDetail.updated")}
                  value={formatDateTime(wearable.updated_at)}
                />
              </div>
            </div>

            <StatusControl wearable={wearable} onUpdated={handleUpdated} />
          </div>

          {/* Right: validation flow + attempt/session history */}
          <div className="flex flex-col gap-4 lg:col-span-2">
            <ValidationFlow wearable={wearable} onUpdated={handleUpdated} />
            <SessionHistory
              bleMac={wearable.ble_mac}
              onSelectSession={setOpenSessionId}
            />
            <AttemptHistory key={historyKey} bleMac={wearable.ble_mac} />
          </div>
        </div>
      </main>

      <WearableFormModal
        show={showEdit}
        onClose={() => setShowEdit(false)}
        wearable={wearable}
        onSaved={(updated) => setWearable((prev) => ({ ...prev, ...updated }))}
      />

      <SessionWearablesModal
        show={Boolean(openSessionId)}
        onClose={() => setOpenSessionId(null)}
        sessionId={openSessionId}
        currentBleMac={wearable.ble_mac}
      />
    </div>
  );
};

export default WearableDetail;
